import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ArticleDetailDto,
  ReactionValue,
} from '@zoxo/shared';

import { api } from '../../lib/api';
import { articleDetailKey } from '../articles/use-article';

/** 반응 대상. 서베이 필라를 폐기해(PRD §2.4) 기사만 남았다. */
export type ReactionTarget = { kind: 'post' | 'article'; id: string };

/** The reaction slice shared by both detail DTOs. */
type Reactionish = Pick<ArticleDetailDto, 'likeCount' | 'dislikeCount' | 'myReaction'>;

type Detail = ArticleDetailDto;

/** Re-derive counts when the viewer's reaction moves from `prev` to `next`. */
function applyReaction(d: Reactionish, next: ReactionValue | null): Reactionish {
  let { likeCount, dislikeCount } = d;
  if (d.myReaction === 'like') likeCount -= 1;
  if (d.myReaction === 'dislike') dislikeCount -= 1;
  if (next === 'like') likeCount += 1;
  if (next === 'dislike') dislikeCount += 1;
  return { likeCount, dislikeCount, myReaction: next };
}

/**
 * Reaction set/clear (PUT/DELETE /articles/:id/reaction).
 * State-set + idempotent: `set('like')` switches sides,
 * `clear()` removes. Optimistically updates the detail cache (counts + myReaction)
 * for instant feedback, then reconciles with the authoritative refetch.
 */
export function useReaction(target: ReactionTarget) {
  const queryClient = useQueryClient();
  const key = articleDetailKey(target.id, true);
  const base = `/articles/${target.id}/reaction`;

  const optimistic = (next: ReactionValue | null) => async () => {
    await queryClient.cancelQueries({ queryKey: key });
    const prev = queryClient.getQueryData<Detail>(key);
    if (prev) {
      queryClient.setQueryData<Detail>(key, { ...prev, ...applyReaction(prev, next) });
    }
    return { prev };
  };

  const rollback = (_e: unknown, _v: unknown, ctx: { prev?: Detail } | undefined) => {
    if (ctx?.prev) queryClient.setQueryData(key, ctx.prev);
  };
  const settle = () => queryClient.invalidateQueries({ queryKey: key });

  const set = useMutation({
    mutationFn: (value: ReactionValue) => api.put<void>(base, { value }),
    onMutate: (value) => optimistic(value)(),
    onError: rollback,
    onSettled: settle,
  });

  const clear = useMutation({
    mutationFn: () => api.del<void>(base),
    onMutate: optimistic(null),
    onError: rollback,
    onSettled: settle,
  });

  return { set, clear };
}
